import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import s from '@components/Questionnaire/form/List/List.module.scss';
import CopyButton from '@components/CopyButton';
import Copy from '@components/Icons/Copy';
import { Select } from '@components/DesignLibrary/Atoms';
import Button from '@components/DesignLibrary/Button';
import Modal from '@components/Modal';
import { Questionnaire } from '@typings/Questionnaire';
import uniq from 'lodash/uniq';
import removeNullish from '@helpers/removeNullish';
import get from 'lodash/get';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { getValidationRules } from '@helpers/validation/schema';
import { FormFieldProps } from '@components/Questionnaire/form/FormField/types';
import Header from '@components/DesignLibrary/Header';
import getDisplayValue from '@components/Questionnaire/form/List/getDisplayValue';
import Spacer from '@components/Spacer';
import { isEqual } from 'lodash';
import evaluateExpression from '@helpers/evaluateExpression';
import getFieldValue from '@helpers/questionnaires/getFieldValue';

type Props = {
  field: Questionnaire.FieldCommon;
  index: number;
  setIndex: (index?: number) => void;
  isPreview: boolean;
  removeListItem?: (index: number) => void;
  fieldsAutofilling?: string[];
  linkSourceFieldKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  onSave?: (newValue: { _id: string }) => void;
  FormFieldComponent: FC<FormFieldProps>;
};

const EditListItemModalOnSave: FC<Props> = ({
  field: initialField,
  index,
  isPreview,
  setIndex,
  removeListItem,
  fieldsAutofilling,
  linkSourceFieldKey,
  onSave,
  FormFieldComponent,
}) => {
  const { fieldsByKey } = useQuestionnaireContext();
  const { formMethods } = useQuestionnaireFormContext();
  const [field] = useState(initialField);

  const [currentListSubsection, setCurrentListSubsection] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [referencedListData, setReferencedListData] = useState<any>(null);
  const [markingAsDuplicate, setMarkingAsDuplicate] = useState(false);
  const [localFormInitialized, setLocalFormInitialized] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>({});

  const getItemValues = useCallback(() => formMethods.getValues()[linkSourceFieldKey]?.[index] || {}, [formMethods, linkSourceFieldKey, index]);

  const modalForm = useForm({
    defaultValues: getItemValues(),
  });

  useEffect(() => {
    if (!localFormInitialized) {
      modalForm.reset(getItemValues());
      setLocalFormInitialized(true);
    }
  }, [getItemValues, localFormInitialized, modalForm]);

  const parentListResponses = useMemo(() =>
    formMethods.getValues()[linkSourceFieldKey] || [],
  [formMethods, linkSourceFieldKey]);

  const modalSubFields = useMemo(() => (field.listItemTemplates ?? []).map(subField => ({
    ...subField,
    computedKey: subField.key,
    computedLinkedFields: fieldsByKey[subField.key]?.linkedFields?.filter(
      f => f.linkedFieldTemplateIndex === index,
    ),
    computedListItemTemplates: Object.values(fieldsByKey).filter(
      f => f.listFieldTemplateId === subField.id,
    ),
    originalKey: `${linkSourceFieldKey}[${index}].${subField.key}`,
  })), [field.listItemTemplates, fieldsByKey, index, linkSourceFieldKey]);

  const modalFieldId = useWatch({
    control: modalForm.control,
    name: "_id",
  });

  const modalFieldAssociatedWithId = useWatch({
    control: modalForm.control,
    name: "_associatedWithId",
  });

  // Watch all form values to detect changes for condition evaluation
  const formValues = modalForm.watch();

  // Calculate subsections for the form
  const listSubsections = useMemo(() =>
    uniq(modalSubFields.map((subField) => subField.listSubsection).filter(removeNullish)),
  [modalSubFields]);

  // Initialize the first subsection
  useEffect(() => {
    setCurrentListSubsection(listSubsections[0] || null);
  }, [listSubsections]);

  // Find title and subtitle fields
  const titleField = useMemo(() =>
    modalSubFields.find((f) => f.isTitleField),
  [modalSubFields]);

  const subtitleFields = useMemo(() =>
    modalSubFields.filter((f) => f.isSubtitleField),
  [modalSubFields]);

  // Effect to evaluate visibility conditions for each field
  useEffect(() => {
    const newFieldVisibility = { ...fieldVisibility };
    let hasChanges = false;

    modalSubFields.forEach(subField => {
      const { conditionExpression } = subField;
      if (conditionExpression) {
        const variables: Record<string, unknown> = {};

        (conditionExpression.variables ?? []).forEach((variable) => {
          // Get the value from the local form
          const referencedValue = modalForm.getValues(variable.referenceId);

          variables[variable.variableName] = getFieldValue({
            field: fieldsByKey[variable.referenceId],
            value: variable.selector
              ? get(referencedValue, variable.selector)
              : referencedValue,
          });
        });

        // Evaluate the condition expression
        const result = evaluateExpression({
          expression: conditionExpression.expression,
          id: `FormField-condition-${subField.id}`,
          variables,
        });

        const isVisible = Boolean(result);
        if (newFieldVisibility[subField.id] !== isVisible) {
          newFieldVisibility[subField.id] = isVisible;
          hasChanges = true;
        }
      } else if (newFieldVisibility[subField.id] !== true) {
        // If no condition, field should be visible
        newFieldVisibility[subField.id] = true;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setFieldVisibility(newFieldVisibility);
    }
  }, [modalSubFields, fieldsByKey, formValues, modalForm]);

  // Clean up fields that become invisible
  useEffect(() => {
    modalSubFields.forEach(subField => {
      const prevIsVisible = fieldVisibility[subField.id] !== false;
      const isVisible = fieldVisibility[subField.id] !== false;

      if (prevIsVisible && !isVisible) {
        // Clean up the field when it becomes invisible
        modalForm.setValue(subField.computedKey, null);
        modalForm.unregister(subField.computedKey);
      }

      // Set default values for selection fields
      if ((subField.type === 'selection' || subField.type === 'single-select')
          && subField.options?.some(o => o.isDefault)
          && !modalForm.getValues(subField.computedKey)
          && isVisible) {
        const defaultOption = subField.options.find(o => o.isDefault);
        if (defaultOption) {
          modalForm.setValue(subField.computedKey, defaultOption.key);
        }
      }
    });
  }, [fieldVisibility, modalSubFields, modalForm]);

  // Handler to save changes back to the parent form
  const handleSave = useCallback(() => {
    const modalValues = modalForm.getValues();

    // Update parent form with values from the modal form
    const existingValues = formMethods.getValues()[linkSourceFieldKey] || [];
    const updatedItem = {
      ...existingValues[index],
      ...modalValues,
    };

    // Ensure _id is preserved
    if (!updatedItem._id && existingValues[index]?._id) {
      updatedItem._id = existingValues[index]._id;
    }

    // Create a new array with the updated item
    const updatedValues = [...existingValues];
    updatedValues[index] = updatedItem;

    // Update the parent form
    formMethods.setValue(linkSourceFieldKey, updatedValues, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setIndex(undefined);
    if (onSave && updatedItem) {
      onSave(updatedItem);
    }
  }, [formMethods, index, linkSourceFieldKey, modalForm, onSave, setIndex]);

  const handleReferencedListDataUpdate = useCallback<(data: {
    title: string;
    values: Array<{
      property: string;
      value: string;
    }> | null;
    onEditClick: () => void;
    onClose: () => void;
    type?: 'autofill' | 'ai-autofill' | 'questionnaire';
    isLoading?: boolean;
  } | null) => void>((newData) => {
      setReferencedListData(newData);
    }, []);

  const openModalFromReferencedList = useCallback((referencedField: Questionnaire.FieldList): void => {
    if (referencedField.modalIndex !== undefined) {
      setIndex(referencedField.modalIndex);
    }
  }, [setIndex]);

  const handleFormOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    const {activeElement} = document;
    if (activeElement && !activeElement.classList.contains('localMOda')) {
      event.preventDefault();
      return;
    }
    modalForm.handleSubmit(handleSave)(event);
  };

  const handleCloseFormModal = (): void => {
    setIndex(undefined);
    setReferencedListData(null);
    setLocalFormInitialized(false);
  };

  const createTabClickHandler = useCallback((subsection) => () => {
    setCurrentListSubsection(subsection === currentListSubsection ? null : subsection);
  }, [currentListSubsection]);

  return (
    <Modal
      title={`Edit ${field.label} item`}
      onClose={(): void => {
        const currentLocalValues = modalForm.getValues();
        const originalParentValues = formMethods.getValues()[linkSourceFieldKey]?.[index] || {};
        const hasUnsavedChanges = !isEqual(currentLocalValues, originalParentValues);
        if (hasUnsavedChanges) {
          if (window.confirm('Close without saving? Unsaved changes will be lost.')) {
            handleCloseFormModal();
          }
        } else {
          handleCloseFormModal();
        }
      }}
      referencedListData={referencedListData}
      isWide
      fixedHeight={listSubsections.length > 0 ? "80vh" : undefined}
      key={`${index}-${referencedListData?._id || ''}`}
    >
      <form onSubmit={handleFormOnSubmit}>
        <FormProvider {...modalForm}>
          <div className={s.modal}>
            {listSubsections.length > 0 && (
              <Header
                title={modalForm.getValues()?.[titleField?.key || ''] || field.label}
                description={
                  <>
                    <Spacer size={4} />
                    <p className="caption">
                      {subtitleFields.map((subtitleField, idx) => (
                        <React.Fragment key={subtitleField.id}>
                          <span style={{ color: 'var(--color-gray-75)' }}>
                            {idx !== 0 ? ' · ' : ''}{subtitleField.label}:
                          </span>
                          <span style={{ color: 'var(--color-gray-100)' }}>
                            {getDisplayValue(subtitleField, modalForm.getValues()[subtitleField.key])}
                          </span>
                        </React.Fragment>
                      ))}
                    </p>
                    <Spacer size={4} />
                  </>
                }
                tabs={listSubsections.map((subsection) => ({
                  label: subsection,
                  onClick: createTabClickHandler(subsection),
                  selected: currentListSubsection === subsection,
                }))}
                className={s.modalHeader}
              />
            )}

            {!isPreview && (
              <div className={s.fieldId}>
                <div className={s.fieldIdData}>
                  <div>
                    <p className="caption">Field ID</p>
                    <p className="caption">{modalFieldId}</p>
                  </div>

                  {parentListResponses.length > 1 && (
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() => setMarkingAsDuplicate(prev => !prev)}
                      className={modalFieldAssociatedWithId ? undefined : s.markAsDuplicate}
                    >
                      {markingAsDuplicate ? 'Hide' : `Mark${modalFieldAssociatedWithId ? 'ed' : ''} as duplicate`}
                    </Button>
                  )}

                  <CopyButton text={modalFieldId} className={s.copyIDButton}>
                    <Copy size={16} /> Copy
                  </CopyButton>
                </div>

                {markingAsDuplicate && (
                  <Select
                    compact
                    label="This field is a duplicate of:"
                    options={[
                      {
                        key: 'no',
                        label: 'None',
                      },
                      ...(parentListResponses as Array<{ _id: string; _associatedWithId: string | null }>)
                        .filter(subField => subField?._id !== parentListResponses[index]?._id && !subField._associatedWithId)
                        .map(subField => ({
                          key: subField._id,
                          label: subField._id,
                        })),
                    ]}
                    optional
                    placeholder='None'
                    value={modalFieldAssociatedWithId}
                    onChange={(duplicateId) => {
                      const associatedWithId = duplicateId === 'no' ? null : duplicateId;
                      modalForm.setValue("_associatedWithId", associatedWithId);
                    }}
                  />
                )}
              </div>
            )}

            <div className={s.modalContent} {...listSubsections.length === 0 && { style: { minHeight: 'unset' } }}>
              {
                modalSubFields.reduce<JSX.Element[]>((acc, subField) => {
                  const isVisible = fieldVisibility[subField.id] !== false;
                  if (isVisible && (!currentListSubsection || subField.listSubsection === currentListSubsection)) {
                    acc.push(<FormFieldComponent
                      key={subField.id}
                      index={0}
                      listField={field}
                      field={{
                        ...subField,
                        key: subField.computedKey,
                        linkedFields: subField.computedLinkedFields,
                        listItemTemplates: subField.computedListItemTemplates,
                      }}
                      control={modalForm.control}
                      rules={getValidationRules(subField)}
                      isPreview={isPreview}
                      isEditing={parentListResponses[index]?._existsInDb}
                      fieldsAutofilling={fieldsAutofilling}
                      handleReferencedListDataUpdate={handleReferencedListDataUpdate}
                      openModalFromReferencedList={openModalFromReferencedList}
                    />);
                  }
                  return acc;
                }, [])
              }
            </div>

            {!isPreview && !field.disabled && (
              <div className={s.saveButton}>
                <Button
                  variant="primary"
                  size="md"
                  type="submit"
                  className="localMOda"
                >
                  Save
                </Button>

                {removeListItem && (
                  <Button
                    variant="destructive"
                    size="md"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to remove this item?')) {
                        removeListItem(index);
                        setIndex(undefined);
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        </FormProvider>
      </form>
    </Modal>
  );
};

export default EditListItemModalOnSave;